import embedCss from '../styles/embed.css?inline'

/**
 * Shadow roots isolate selectors, which is exactly what we want against
 * Webflow's global CSS — but three kinds of rule do NOT work inside one and
 * have to be hoisted to the document:
 *
 *   @font-face   font families defined in a shadow root are never applied
 *   @property    registrations belong to the document's property registry
 *   @import      only valid at the top of a document stylesheet
 *
 * Everything else is scoped into the shadow root, with `:root` rewritten to
 * `:host` (Tailwind's `@theme` emits variables on `:root`, which matches
 * nothing inside a shadow tree).
 *
 * This split happens at runtime rather than in a Vite plugin: it is a pure
 * string transform over a few KB, it runs once per page, and keeping it out of
 * the build pipeline makes it directly unit-testable against real Tailwind
 * output instead of depending on plugin ordering.
 */

const DOCUMENT_AT_RULE = /^@(?:font-face|property|import|charset|namespace)\b/i

/** `:root` in selector position — avoids matching inside strings like `content: ":root"`. */
const ROOT_SELECTOR = /(^|[\s,{}>~+])::?root\b/g

export interface SplitCss {
  /** Rules that must live in `document.head`. */
  documentCss: string
  /** Rules to adopt into every shadow root. */
  shadowCss: string
}

/**
 * Find the end of the top-level construct starting at `start`, respecting
 * strings, escapes and comments so braces inside `content: "}"` don't confuse
 * the depth counter.
 */
function endOfStatement(css: string, start: number): number {
  let depth = 0
  let quote: string | null = null
  let i = start

  while (i < css.length) {
    const char = css[i]

    if (quote) {
      if (char === '\\') {
        i += 2
        continue
      }
      if (char === quote) quote = null
      i++
      continue
    }

    if (char === '"' || char === "'") {
      quote = char
      i++
      continue
    }

    if (char === '/' && css[i + 1] === '*') {
      const end = css.indexOf('*/', i + 2)
      i = end === -1 ? css.length : end + 2
      continue
    }

    if (char === '{') {
      depth++
      i++
      continue
    }

    if (char === '}') {
      depth--
      i++
      if (depth <= 0) return i
      continue
    }

    if (char === ';' && depth === 0) return i + 1

    i++
  }

  return i
}

/** Partition a stylesheet into document-level and shadow-scoped halves. */
export function splitCss(css: string): SplitCss {
  const documentParts: string[] = []
  const shadowParts: string[] = []

  let i = 0
  while (i < css.length) {
    // Skip inter-statement whitespace so chunks start on real content.
    if (/\s/.test(css[i] as string)) {
      i++
      continue
    }

    const end = endOfStatement(css, i)
    const chunk = css.slice(i, end).trim()
    i = end

    if (!chunk) continue
    if (DOCUMENT_AT_RULE.test(chunk)) documentParts.push(chunk)
    else shadowParts.push(chunk)
  }

  return {
    documentCss: documentParts.join('\n'),
    shadowCss: shadowParts.join('\n').replace(ROOT_SELECTOR, '$1:host'),
  }
}

let cachedSplit: SplitCss | null = null

function getSplit(): SplitCss {
  cachedSplit ??= splitCss(embedCss)
  return cachedSplit
}

let documentStylesInjected = false

/**
 * Inject the document-level half exactly once per page, no matter how many
 * components mount. Safe to call repeatedly.
 */
export function ensureDocumentStyles(doc: Document = document): void {
  if (documentStylesInjected) return

  // Another copy of the bundle may already have done this.
  if (doc.querySelector('style[data-cv-doc]')) {
    documentStylesInjected = true
    return
  }

  const { documentCss } = getSplit()
  documentStylesInjected = true
  if (!documentCss) return

  const style = doc.createElement('style')
  style.setAttribute('data-cv-doc', '')
  style.textContent = documentCss
  doc.head.appendChild(style)
}

// `undefined` = not built yet, `null` = constructable stylesheets unavailable.
let sharedSheet: CSSStyleSheet | null | undefined

/**
 * One CSSStyleSheet, constructed once and adopted by every shadow root.
 * Adoption is by reference, so N mounts cost one parse.
 */
function getSharedSheet(): CSSStyleSheet | null {
  if (sharedSheet !== undefined) return sharedSheet

  try {
    const sheet = new CSSStyleSheet()
    sheet.replaceSync(getSplit().shadowCss)
    sharedSheet = sheet
  } catch {
    sharedSheet = null
  }

  return sharedSheet
}

/**
 * Apply component styles to a shadow root, preferring `adoptedStyleSheets` and
 * falling back to an inline `<style>` where constructable stylesheets are
 * missing (chiefly test environments — browser support has been universal
 * since 2023).
 */
export function applyStyles(root: ShadowRoot): void {
  ensureDocumentStyles(root.ownerDocument)

  const sheet = getSharedSheet()
  if (sheet) {
    try {
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet]
      return
    } catch {
      // Fall through to the <style> path.
    }
  }

  const style = root.ownerDocument.createElement('style')
  style.setAttribute('data-cv-shadow', '')
  style.textContent = getSplit().shadowCss
  root.appendChild(style)
}

/** Test helper — clears memoised state so each test starts clean. */
export function resetStyles(): void {
  cachedSplit = null
  sharedSheet = undefined
  documentStylesInjected = false
}
